import { isAfter, isBefore } from 'date-fns';
import type { Poll } from 'models';
import { useTranslation } from 'react-i18next';
import { Checkbox, Col, Radio, RichTextRenderer, Row, Text, Title, message } from 'ui';
import { Attachments } from 'features/attachments';
import { Translation } from 'features/translations';
import { useAddVoteMutation, useRemoveVoteMutation } from '../queries';

type P = {
  isBookmark?: boolean;
  isDisabled: boolean;
  poll: Poll;
  showChoicePercentage: boolean;
};

const getPercentage = (finalNumber: number, percentageNumber: number) =>
  Math.round(((percentageNumber * 100) / finalNumber) * 100) / 100;

const onVoteError = (error: unknown) => {
  console.error(error);
  message.error('Failed to add vote.');
};

const PollRendererCore = ({ isBookmark = false, isDisabled, poll: pollData, showChoicePercentage }: P) => {
  const { mutate: addVote } = useAddVoteMutation();
  const { mutate: removeVote } = useRemoveVoteMutation();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  const onVote = (event: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    if (event.target.checked) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      addVote({ choice: event.target.value, pollId: pollData.id }, { onError: onVoteError });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      removeVote({ choice: event.target.value, pollId: pollData.id }, { onError: onVoteError });
    }
  };

  const isFinished = isAfter(new Date(), pollData.endTime);
  const isNotStarted = isBefore(new Date(), pollData.startTime);

  const { t } = useTranslation();

  return (
    <Row className="poll-core" gutter={16}>
      {pollData.title ? (
        <Col span={24}>
          <Title level={4} marginBottom>
            {pollData.title}
          </Title>
        </Col>
      ) : null}
      {pollData.htmlDescription ? (
        <Col span={24}>
          <RichTextRenderer data={pollData.htmlDescription} />
          {pollData.description && pollData.id ? <Translation id={pollData.id} type="poll" /> : null}
        </Col>
      ) : null}
      {isFinished ? (
        <Col span={24} style={{ marginTop: 30 }}>
          <Title level={4} marginBottom>
            Poll has ended
          </Title>
        </Col>
      ) : null}
      {isNotStarted ? (
        <Col span={24} style={{ marginTop: 30 }}>
          <Title level={4} marginBottom>
            Poll not started yet
          </Title>
        </Col>
      ) : null}
      <Col className={`poll-core__poll ${isBookmark ? 'poll-core__disabled' : ''}`} span={24}>
        {pollData.questions.map((item) => {
          const votes = item.questionVotes;
          return (
            <div className="poll-core__poll__item" key={item.id}>
              <Text size="large" strong>
                {item.question}
              </Text>
              <Text size="small" strong style={{ marginTop: 25 }}>
                {item.multiple ? t('Please select all that apply') : t('Please select one')}
              </Text>

              {item.multiple ? (
                <div className="poll-core__choices">
                  <Checkbox.Group
                    defaultValue={item.choices.filter((choice) => choice.voted).map((choice) => choice.id)}
                    disabled={isFinished || isNotStarted || isDisabled || isBookmark}
                  >
                    {item.choices.map((choice) => (
                      <Row gutter={13} key={choice.id}>
                        <Col flex="1">
                          <Checkbox className="poll-core__choice" onChange={onVote} value={choice.id}>
                            {choice.name}
                          </Checkbox>
                        </Col>
                        {isFinished || showChoicePercentage ? (
                          <Col className="poll-core__choices__percentage" flex="0 0 50px">
                            <Text weight="bold">
                              {votes === 0 ? choice.votesCount : getPercentage(votes, choice.votesCount)}%
                            </Text>
                          </Col>
                        ) : null}
                      </Row>
                    ))}
                  </Checkbox.Group>
                </div>
              ) : (
                <div className="poll-core__choices">
                  <Radio.Group
                    defaultValue={item.choices.find((choice) => choice.voted)?.id}
                    disabled={isFinished || isNotStarted || isDisabled || isBookmark}
                    onChange={onVote}
                  >
                    {item.choices.map((choice) => (
                      <Row gutter={13} key={choice.id}>
                        <Col flex="1">
                          <Radio className="poll-core__choice" value={choice.id}>
                            {choice.name}
                          </Radio>
                        </Col>
                        {isFinished || showChoicePercentage ? (
                          <Col className="poll-core__choices__percentage" flex="0 0 50px">
                            {votes === 0 ? choice.votesCount : getPercentage(votes, choice.votesCount)}%
                          </Col>
                        ) : null}
                      </Row>
                    ))}
                  </Radio.Group>
                </div>
              )}
              <Text size="small" strong style={{ marginTop: 15 }}>
                {votes} {t('pollVotes', { count: votes })}
              </Text>
            </div>
          );
        })}
      </Col>
      {pollData.attachments?.length ? (
        <Col className="poll-core__attachments" span={24}>
          <Attachments attachments={pollData.attachments} />
        </Col>
      ) : null}
    </Row>
  );
};

export default PollRendererCore;
